import { useCallback, useMemo } from 'react';
import { t } from '@lingui/macro';

import useDebouncedValue from 'hooks/useDebouncedValue';

import type { SearchableFormValues } from 'components/SearchableFilter';
import useSelectedFilterLabels from '../../../hooks/useSelectedFilterLabels';
import { useOemSegmentsFiltersQuery } from '../useOemSegmentsFiltersQuery';

interface UseOemSegmentsFiltersProps {
  value: string[];
  onSubmit: (data: SearchableFormValues) => void;
}

export default function useOemSegmentsFilters({
  value,
  onSubmit,
}: UseOemSegmentsFiltersProps) {
  const [searchValue, setSearchString, debouncedSearchValue] =
    useDebouncedValue('');

  const {
    data: oemSegmentsData,
    loading,
    loadMore,
    loadingMore,
  } = useOemSegmentsFiltersQuery({
    variables: { page: 1, text: debouncedSearchValue },
  });

  const onSearch = useCallback(
    (val: string) => {
      setSearchString(val);
    },
    [setSearchString],
  );

  const items = useMemo(
    () =>
      (oemSegmentsData?.managedOemSegments.collection ?? []).map((record) => ({
        id: record.id,
        label: record.name,
        value: record.id,
      })),
    [oemSegmentsData],
  );

  const labelsById = useSelectedFilterLabels(items);

  // When a single segment is pre-selected but is not part of the currently
  // loaded results (e.g. it lives on a later page), fetch it by id so the
  // filter button can show its name instead of the generic fallback.
  const selectedId = value.length === 1 ? value[0] : undefined;

  const { data: selectedData } = useOemSegmentsFiltersQuery({
    variables: {
      page: 1,
      text: '',
      ids: selectedId ? [selectedId] : [],
    },
    skip:
      !selectedId ||
      !oemSegmentsData ||
      items.some((item) => item.value === selectedId) ||
      Boolean(labelsById[selectedId]),
  });

  const selectedLabel = selectedData?.managedOemSegments.collection.find(
    (record) => record.id === selectedId,
  )?.name;

  const text = useMemo(() => {
    if (value.length === 0) return t`All OEM Segments`;

    if (value.length === 1)
      return (
        labelsById[value[0]] ||
        items.find((item) => item.value === value[0])?.label ||
        selectedLabel ||
        t`OEM Segment`
      );

    return t`OEM Segments (${value.length})`;
  }, [labelsById, items, selectedLabel, value]);

  return {
    items,
    text,
    loading,
    searchValue,
    debouncedSearchValue,
    onSearch,
    mode: 'async' as const,
    value,
    onSubmit,
    onLoadMore: loadMore,
    loadingMore,
  };
}
